import React, { forwardRef, useId, useRef } from 'react';
import { Box, Flex, Spinner, VisuallyHidden } from '@chakra-ui/react';
import { MotionBox } from '@olo-web/components/atoms/motion/MotionBox.ui';
import { AnimateSharedLayout } from 'framer-motion';

export type TPillTabs = {
  value: any;
  items: { label: JSX.Element | string; value: any; disabled?: boolean }[];
  onChange: (value: any) => void;
  width?: string | Record<string, string>;
  isDisabled?: boolean;
  isLoading?: boolean;
  /** When true, sets aria-haspopup="dialog" on that tab (e.g. delivery opens an address dialog). */
  getItemAriaHasPopup?: (itemValue: any) => boolean;
  /**
   * Receives the currently-checked radio input. Use as `finalFocusRef` for any
   * dialog this radio group opens, so focus returns to the active tab stop on dismiss.
   */
  triggerFocusRef?: React.MutableRefObject<HTMLInputElement | null>;
};

const PillTabsInner = (
  {
    items,
    value,
    onChange,
    width = '100%',
    isDisabled,
    isLoading,
    getItemAriaHasPopup,
    triggerFocusRef,
  }: TPillTabs,
  ref: React.ForwardedRef<HTMLFieldSetElement>
) => {
  const radioGroupName = useId();
  const radioInputs = useRef(new Map<string, HTMLInputElement>());
  const setRadioRef =
    (itemValue: string, isSelected: boolean) => (node: HTMLInputElement | null) => {
      if (!node) return;
      radioInputs.current.set(itemValue, node);
      if (isSelected && triggerFocusRef) {
        triggerFocusRef.current = node;
      }
    };

  // Also assigned during render: a dialog unmounting in the same commit restores
  // focus in the mutation phase, before ref callbacks re-attach in the layout phase.
  const selectedRadioInput = radioInputs.current.get(String(value));
  if (triggerFocusRef && selectedRadioInput) {
    triggerFocusRef.current = selectedRadioInput;
  }

  return (
    <Box as="fieldset" ref={ref} border="none" p={0} m={0} w={width} minW={0} data-testid="toggle">
      <VisuallyHidden as="legend">Order type selection</VisuallyHidden>
      <Flex bg="blackAlpha.100" borderRadius="22px" w="100%" align="stretch" overflow="visible">
        <AnimateSharedLayout>
          {items.map((item, index) => {
            const isSelected = value === item.value;
            const itemDisabled = Boolean(isDisabled || isLoading || item.disabled);
            const buttonColor = itemDisabled
              ? 'blackAlpha.300'
              : isSelected
              ? 'white'
              : 'blackAlpha.900';

            return (
              <Box
                key={String(item.value) + index}
                as="label"
                position="relative"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flex="1"
                minW={0}
                borderRadius="22px"
                cursor={itemDisabled ? 'not-allowed' : 'pointer'}
                _focusWithin={
                  itemDisabled
                    ? undefined
                    : {
                        zIndex: 1,
                        boxShadow: 'outline',
                      }
                }
              >
                {isSelected && (
                  <MotionBox
                    layoutId="tab-pill"
                    zIndex={0}
                    bg={isDisabled ? 'blackAlpha.200' : 'primary.350'}
                    borderColor="gray.200"
                    h="44px"
                    borderRadius="22px"
                    borderStyle="solid"
                    borderWidth="4px"
                    pos="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                  />
                )}
                <VisuallyHidden as="span">
                  <input
                    ref={setRadioRef(String(item.value), isSelected)}
                    type="radio"
                    name={radioGroupName}
                    value={String(item.value)}
                    checked={isSelected}
                    onChange={() => onChange(item.value)}
                    disabled={itemDisabled}
                    aria-haspopup={getItemAriaHasPopup?.(item.value) ? 'dialog' : undefined}
                  />
                </VisuallyHidden>
                <Box
                  pos="relative"
                  zIndex={2}
                  color={buttonColor}
                  fontWeight={isSelected ? '600' : '400'}
                  h="44px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  w="100%"
                  userSelect="none"
                >
                  {item.label}
                  {isLoading && !isSelected ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      pos="absolute"
                      top={0}
                      right={0}
                      left={0}
                      bottom={0}
                      bg="rgba(255,255,255,0.5)"
                      filter="blur(1px)"
                    >
                      <Spinner />
                    </Box>
                  ) : null}
                </Box>
              </Box>
            );
          })}
        </AnimateSharedLayout>
      </Flex>
    </Box>
  );
};

export const PillTabs = forwardRef(PillTabsInner);
